import { ReactNode, useEffect } from 'react';
import { MsalAuthenticationTemplate, MsalProvider } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { authService } from '@core/services/authService';
import { IFrameLoginPage } from '@core/pages/iFrameLoginPage/iFrameLoginPage';
import { inIframe } from '@shared/utils';
import useUserStore from '@core/stores/useUserStore';
import { logger } from '@shared/logger';

const SCOPE_NAME = 'AuthProvider';

/**
 * A component that protects the authentication flow in an iframe.
 * Renders the login page if the user is not authenticated and inside an iframe.
 *
 * @param {object} props - The component props
 * @param {ReactNode} props.children - The child components to render after authentication
 * @returns The login page or the wrapped children components based on authentication state.
 */
const IFrameGuard = ({ children }: { children: ReactNode }) => {
    const token = useUserStore((state) => state.token); // Fetches the current user's token from the store
    const activeAccount = authService.getActiveAccount(); // Gets the active account from the authentication service

    // If in an iframe and no token or active account is found, render the login page
    if (inIframe() && !token && !activeAccount) {
        logger.log(SCOPE_NAME, 1, 'Render IFrameLoginPage', !token, !activeAccount);
        return <IFrameLoginPage loginClick={() => authService.loginPopup()} />;
    }

    return (
        <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
            <AuthConsumer>{children}</AuthConsumer>
        </MsalAuthenticationTemplate>
    );
};

/**
 * A component that consumes authentication state and ensures token validation.
 * Triggers token refresh if an active account is present.
 *
 * @param {object} props - The component props
 * @param {ReactNode} props.children - The child components to render once authentication is validated
 * @returns The child components after authentication validation.
 */
const AuthConsumer = ({ children }: { children: ReactNode }) => {
    const activeAccount = authService.getActiveAccount(); // Gets the active account from the authentication service

    useEffect(() => {
        // If an active account exists, start token validation and refresh if necessary
        if (activeAccount) {
            logger.log(SCOPE_NAME, 1, 'Start validateToken');
            authService.refreshToken(false, true);
        }
    }, [activeAccount]); // Effect runs when the activeAccount changes

    return children; // Renders the children components after authentication validation
};

/**
 * The top-level authentication provider component that wraps children components.
 * It ensures that the MSAL authentication context is available to all children.
 *
 * @param {object} props - The component props
 * @param {ReactNode} props.children - The child components to render inside the authentication provider
 * @returns  The children wrapped with the MsalProvider and IFrameGuard components.
 */
export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const instance = authService.getMsalInstance(); // Gets the MSAL instance from the authentication service

    return (
        <MsalProvider instance={instance}>
            <IFrameGuard>{children}</IFrameGuard>
        </MsalProvider>
    );
};
